import { Popover } from '@headlessui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { useTenantContext } from '@/hooks/useTenantContext'
import type { NotificationFeed } from '@/types/notifications'
import { HeaderButton } from '@/components/carbon/HeaderButton'
import { IconBell } from '@/components/carbon/icons'
import { NotificationBadge } from '@/components/carbon/NotificationBadge'
import NotificationsDropdown from './NotificationsDropdown'

/**
 * Header bell (a 48px HeaderButton). Surfaces the unread count across every
 * page in the app as a neutral inverse badge, never a colored dot.
 *
 * Lifecycle:
 *   - `useQuery` polls the feed every 60s; 30s staleTime keeps re-opens snappy.
 *   - `retry: 1` keeps the bell silent if the backend 404s (notifications
 *     endpoints not deployed yet) — the bell still renders, just with no
 *     badge, so it never crashes the top-bar.
 *   - Mutations are optimistic: dismiss removes the item from cache and
 *     mark-all-read flips every visible row's `isRead`. We rollback from a
 *     snapshot on error and always `invalidateQueries` on settle so the next
 *     refetch reconciles.
 *
 * Wrapping component is a Headless UI v1 `Popover`. The panel is fixed under
 * the 48px header (the header row clips its overflow; a fixed panel escapes
 * that clip): full width on phones, 380px under the bell from 672px. It sits
 * in the Gray 100 shell zone (data-shell) as a menu surface (data-menu).
 */
export default function NotificationsBell() {
  const queryClient = useQueryClient()
  const { defaultTenantId } = useTenantContext()

  // Global-admin "All Tenants" state is signalled by `defaultTenantId ===
  // undefined`. The notifications API is tenant-scoped, so we skip the query
  // entirely in that mode rather than fabricating a global feed.
  const isQueryEnabled = Boolean(defaultTenantId)

  const queryKey = ['notifications', defaultTenantId ?? 'none'] as const

  const { data: feed } = useQuery<NotificationFeed>({
    queryKey,
    queryFn: () => api.getNotifications(defaultTenantId as string),
    enabled: isQueryEnabled,
    // Background poll keeps the badge live without thrashing the API. 60s is
    // a deliberate compromise — credentials don't change minute-to-minute,
    // and a faster cadence would just produce console noise during dev.
    refetchInterval: 60_000,
    staleTime: 30_000,
    // Single retry. The backend may genuinely be down (Wave 3 endpoints not
    // deployed yet) — we don't want to spam the network tab.
    retry: 1,
    refetchOnWindowFocus: false,
  })

  /** Optimistic single dismissal. */
  const dismissMutation = useMutation({
    mutationFn: ({ tenantId, key }: { tenantId: string; key: string }) =>
      api.dismissNotification(tenantId, key),
    onMutate: async ({ key }) => {
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<NotificationFeed>(queryKey)
      if (previous) {
        const item = previous.items.find(i => i.key === key)
        const wasUnread = item && !item.isRead
        const remaining = previous.items.filter(i => i.key !== key)
        queryClient.setQueryData<NotificationFeed>(queryKey, {
          ...previous,
          items: remaining,
          unreadCount: Math.max(0, previous.unreadCount - (wasUnread ? 1 : 0)),
        })
      }
      return { previous }
    },
    onError: (_err, _vars, context) => {
      // Rollback to the snapshot taken in onMutate. `context` is undefined if
      // onMutate threw before returning (defensive).
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous)
      }
    },
    onSettled: () => {
      // Always reconcile with the server — covers both success (server may
      // have noticed the source row resolved before we did) and error.
      queryClient.invalidateQueries({ queryKey })
    },
  })

  /** Optimistic bulk mark-all-read. Operates on whatever's currently cached. */
  const markAllReadMutation = useMutation({
    mutationFn: ({ tenantId, keys }: { tenantId: string; keys: string[] }) =>
      api.markNotificationsRead(tenantId, keys),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<NotificationFeed>(queryKey)
      if (previous) {
        queryClient.setQueryData<NotificationFeed>(queryKey, {
          ...previous,
          items: previous.items.map(item => ({ ...item, isRead: true })),
          unreadCount: 0,
        })
      }
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })

  const handleDismiss = (key: string) => {
    if (!defaultTenantId) return
    dismissMutation.mutate({ tenantId: defaultTenantId, key })
  }

  const handleMarkAllRead = () => {
    if (!defaultTenantId || !feed) return
    const unreadKeys = feed.items.filter(i => !i.isRead).map(i => i.key)
    if (unreadKeys.length === 0) return
    markAllReadMutation.mutate({ tenantId: defaultTenantId, keys: unreadKeys })
  }

  const unread = feed?.unreadCount ?? 0

  return (
    <Popover className="flex flex-none">
      {({ open, close }) => (
        <>
          <Popover.Button
            as={HeaderButton}
            label={unread > 0 ? `Notifications, ${unread} new` : 'Notifications'}
            icon={<IconBell />}
            badge={<NotificationBadge count={unread} />}
            active={open}
          />
          <Popover.Panel
            data-shell=""
            data-menu=""
            className="fixed inset-x-0 top-12 z-40 c-md:left-auto c-md:right-12"
          >
            <NotificationsDropdown
              feed={feed}
              onDismiss={handleDismiss}
              onMarkAllRead={handleMarkAllRead}
              onClose={close}
            />
          </Popover.Panel>
        </>
      )}
    </Popover>
  )
}